'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { useState, type ReactNode } from 'react';
import { isApiError } from '../lib/api/error';
import { getAuthSession } from '../lib/authSession';
import {
  createIndexedDbQueryPersister,
  getQueryPersistenceBuster,
  queryPersistenceMaxAgeMs,
  shouldPersistQuery,
} from './queryPersister';

type QueryProviderProps = {
  children: ReactNode;
};

export const QueryProvider = ({ children }: QueryProviderProps) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 5 * 60 * 1000,
            retry: (failureCount, error) => {
              if (isApiError(error) && error.statusCode === 401) {
                return false;
              }
              return failureCount < 1;
            },
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchInterval: false,
          },
        },
      }),
  );
  const [persister] = useState(() => createIndexedDbQueryPersister());
  const [buster] = useState(() => getQueryPersistenceBuster(getAuthSession()?.member.id));

  if (!persister) {
    return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
  }

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: queryPersistenceMaxAgeMs,
        buster,
        dehydrateOptions: {
          shouldDehydrateMutation: () => false,
          shouldDehydrateQuery: shouldPersistQuery,
        },
      }}
    >
      {children}
    </PersistQueryClientProvider>
  );
};
